export const IgFontFamily = {
  heading: ['"Inter"', "sans-serif"].join(","),
  body: ['"Inter"', "sans-serif"].join(","),
  code: ["JetBrains Mono", "monospace"].join(","),
} as const;

// Brand colors
export const StripeColor = "#4F46E5" as const;
export const ShopifyColor = "#96bf48" as const;

// Shadows
export const IgShadows = {
  low: "0px 4px 12px 0px rgba(0, 0, 0, 0.06), 0px 2px 6px 0px rgba(0, 0, 0, 0.04)",
  /** Figma effect style `shadow/md` — menus and other small popovers. */
  md: "0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
  high: "0px 10px 38px -10px rgba(13, 18, 22, 0.35), 0px 10px 20px -15px rgba(14, 18, 22, 0.20)",
} as const;

// Color primitives
export const IgGrayPrimitives = {
  50: "#FFFFFF",
  100: "#FDFDFD",
  200: "#FAFAFA",
  300: "#F7F7F7",
  400: "#F3F3F3",
  500: "#F1F1F1",
  600: "#EBEBEB",
  700: "#E3E3E3",
  800: "#D4D4D4",
  900: "#CCCCCC",
  A100: "#B5B5B5",
  A200: "#8A8A8A",
  A300: "#616161",
  A400: "#4A4A4A",
  A500: "#303030",
  A600: "#1A1A1A",
} as const;

export const IgBasePrimitives = {
  50: "#EEF4FF",
  100: "#D9E6FF",
  200: "#BCD3FF",
  300: "#8EB8FF",
  400: "#5991FF",
  500: "#2962FF", // Intelligems Brand Blue
  600: "#1B46F5",
  700: "#1433E1",
  800: "#172AB6",
  900: "#192A8F",
  A100: "#141B57",
  A200: "#3E6AC7",
} as const;

export const IgBluePrimitives = {
  50: "#F4F7FD",
  100: "#E9F0FB",
  200: "#DDE7F8",
  300: "#CFDDF4",
  400: "#C0D1F0",
  500: "#B1C5EB",
  600: "#A0B6E4",
  700: "#8FA6DC",
  800: "#7D95D4",
  900: "#6C85CD",
  A100: "#5D76C8",
  A200: "#3E6AC7",
  A300: "#355DB0",
  A400: "#2D5199",
  A500: "#243D73",
  A600: "#1A2A4D",
} as const;

export const IgGreenPrimitives = {
  50: "#F8FFFB",
  100: "#E3FFED",
  200: "#CDFEE1",
  300: "#B4FED2",
  400: "#92FEC2",
  500: "#63FDB0",
  600: "#38FAA3",
  700: "#35EE9B",
  800: "#32E193",
  900: "#2ED389",
  A100: "#32A06E",
  A200: "#29845A",
  A300: "#136F45",
  A400: "#0C5132",
  A500: "#083D25",
  A600: "#092A1B",
} as const;

export const IgOrangePrimitives = {
  50: "#FFFDFA",
  100: "#FFF7EE",
  200: "#FFF1E3",
  300: "#FFEBD5",
  400: "#FFE4C6",
  500: "#FFDDB6",
  600: "#FFD6A4",
  700: "#FFC879",
  800: "#FFB800",
  900: "#E5A500",
  A100: "#B28400",
  A200: "#956F00",
  A300: "#7C5800",
  A400: "#5E4200",
  A500: "#412D00",
  A600: "#251A00",
} as const;

export const IgRedPrimitives = {
  50: "#FFFBFB",
  100: "#FFF6F6",
  200: "#FFEDEC",
  300: "#FEE9E8",
  400: "#FEE2E1",
  500: "#FEDAD9",
  600: "#FED3D1",
  700: "#FEC3C1",
  800: "#FDB0AC",
  900: "#FD817A",
  A100: "#EF4D2F",
  A200: "#E51C00",
  A300: "#B5260B",
  A400: "#8E1F0B",
  A500: "#5F1507",
  A600: "#2F0A04",
} as const;

export const IgAzurePrimitives = {
  50: "#FBFDFF",
  100: "#F2F9FF",
  200: "#EAF4FF",
  300: "#E0F0FF",
  400: "#D5EBFF",
  500: "#CAE6FF",
  600: "#C0E1FF",
  700: "#A8D8FF",
  800: "#91D0FF",
  900: "#51C0FF",
  A100: "#0094D5",
  A200: "#007CB4",
  A300: "#00679B",
  A400: "#00527C",
  A500: "#003A5A",
  A600: "#002133",
} as const;

export const IgLimePrimitives = {
  50: "#F7FEE7",
  100: "#ECFCCB",
  200: "#D9F99D",
  300: "#BEF264",
  400: "#A3E635",
  500: "#84CC16",
  600: "#65A30D",
  700: "#4D7C0F",
  800: "#365314",
  900: "#1A2E05",
  A100: "#14220A",
  A200: "#0D1804",
} as const;

export const IgPurplePrimitives = {
  50: "#FAF8FF",
  100: "#F4F0FF",
  200: "#EDE6FF",
  300: "#E0D1FF",
  400: "#D0BBFF",
  500: "#BEA6FF",
  600: "#A78BFE",
  700: "#8B5CF6",
  800: "#7C3AED",
  900: "#6D28D9",
  A100: "#5B21B6",
  A200: "#4C1D95",
} as const;

export const IgSlatePrimitives = {
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
  A100: "#0A0F1A",
  A200: "#050A0F",
} as const;

export const IgIndigoPrimitives = {
  50: "#EEF2FF",
  100: "#E0E7FF",
  200: "#C7D2FE",
  300: "#A5B4FC",
  400: "#818CF8",
  500: "#6366F1",
  600: "#4F46E5",
  700: "#4338CA",
  800: "#3730A3",
  900: "#312E81",
  A100: "#1E1B4B",
  A200: "#09090B",
} as const;

export const IgVioletPrimitives = {
  50: "#FDF4FF",
  100: "#FAE8FF",
  200: "#F3D4FE",
  300: "#E9B3FD",
  400: "#D946EF",
  500: "#C026D3",
  600: "#A21CAF",
  700: "#86198F",
  800: "#701A75",
  900: "#581C87",
  A100: "#4A1C6B",
  A200: "#3B1A4F",
} as const;

export const IgPinkPrimitives = {
  50: "#FDF2F8",
  100: "#FCE7F3",
  200: "#FBCFE8",
  300: "#F9A8D4",
  400: "#F472B6",
  500: "#EC4899",
  600: "#DB2777",
  700: "#BE185D",
  800: "#9D174D",
  900: "#831843",
  A100: "#6B1835",
  A200: "#500724",
} as const;

export const IgRosePrimitives = {
  50: "#FFF1F2",
  100: "#FFE4E6",
  200: "#FECDD3",
  300: "#FDA4AF",
  400: "#FB7185",
  500: "#F43F5E",
  600: "#E11D48",
  700: "#BE123C",
  800: "#9F1239",
  900: "#881337",
  A100: "#7D161A",
  A200: "#4C0F1A",
} as const;

export const IgAmberPrimitives = {
  50: "#FFFBEB",
  100: "#FEF3C7",
  200: "#FDE68A",
  300: "#FCD34D",
  400: "#FBBF24",
  500: "#F59E0B",
  600: "#D97706",
  700: "#B45309",
  800: "#92400E",
  900: "#78350F",
  A100: "#713F12",
  A200: "#5C2A0A",
} as const;

export const IgYellowPrimitives = {
  50: "#FEFCE8",
  100: "#FEF9C3",
  200: "#FEF08A",
  300: "#FDE047",
  400: "#FACC15",
  500: "#EAB308",
  600: "#CA8A04",
  700: "#A16207",
  800: "#854D0E",
  900: "#713F12",
  A100: "#5A2D0C",
  A200: "#3F1A06",
} as const;

export const IgTealPrimitives = {
  50: "#F0FDFA",
  100: "#CCFBF1",
  200: "#99F6E4",
  300: "#5EEAD4",
  400: "#2DD4BF",
  500: "#14B8A6",
  600: "#0D9488",
  700: "#0F766E",
  800: "#115E59",
  900: "#134E4A",
  A100: "#0F3D3A",
  A200: "#0A2E2A",
} as const;

export const IgCyanPrimitives = {
  50: "#ECFEFF",
  100: "#CFFAFE",
  200: "#A5F3FC",
  300: "#67E8F9",
  400: "#22D3EE",
  500: "#06B6D4",
  600: "#0891B2",
  700: "#0E7490",
  800: "#155E75",
  900: "#164E63",
  A100: "#0F3A46",
  A200: "#083344",
} as const;

export const IgCoralPrimitives = {
  50: "#FFF8F5",
  100: "#FFEDE5",
  200: "#FFD6CC",
  300: "#FFB8A3",
  400: "#FF9478",
  500: "#FF6B47",
  600: "#FF4D1A",
  700: "#E63946",
  800: "#C5282F",
  900: "#A11D20",
  A100: "#580F12",
  A200: "#3F1C06",
} as const;

// Variation colors
export const VariationColors = [
  IgBluePrimitives["A200"],
  IgGreenPrimitives["A100"],
  IgBasePrimitives[500],
  "#9C4DDD",
  IgOrangePrimitives[800],
  IgAzurePrimitives[800],
] as const;

// Text colors
export const IgTonalTextColors = {
  info: IgAzurePrimitives["A200"],
  success: IgGreenPrimitives["A300"],
  warning: IgOrangePrimitives["A300"],
  error: IgRedPrimitives["A200"],
} as const;

export const IgInteractiveTextColors = {
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[600],
    focused: IgBluePrimitives["A200"],
  },
  dark: {
    default: IgGrayPrimitives[100],
    hover: IgGrayPrimitives[500],
    active: IgGrayPrimitives[50],
    disabled: IgGrayPrimitives["A100"],
    focused: IgGrayPrimitives[100],
  },
  neutral: {
    default: IgGrayPrimitives["A500"],
    hover: IgGrayPrimitives["A300"],
    active: IgGrayPrimitives["A600"],
    disabled: IgGrayPrimitives["A200"],
    focused: IgGrayPrimitives["A600"],
  },
} as const;

export const IgCoreTextColors = {
  primary: IgGrayPrimitives["A600"],
  secondary: IgGrayPrimitives["A400"],
  tertiary: IgGrayPrimitives["A300"],
  placeholder: IgGrayPrimitives["A200"],
  dark: IgGrayPrimitives[50],
  accent: IgBluePrimitives["A200"],
  tonal: IgTonalTextColors,
  interactive: IgInteractiveTextColors,
} as const;

// Background colors
export const IgTonalBackgroundColors = {
  info: IgAzurePrimitives[100],
  infoSolid: IgAzurePrimitives["A200"],
  success: IgGreenPrimitives[100],
  successSolid: IgGreenPrimitives["A200"],
  warning: IgOrangePrimitives[200],
  warningSolid: IgOrangePrimitives["A200"],
  error: IgRedPrimitives[100],
  errorSolid: IgRedPrimitives["A100"],
} as const;

export const IgInteractiveBackgroundColors = {
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[600],
    focused: IgBluePrimitives["A500"],
  },
  neutral: {
    default: IgGrayPrimitives[300],
    hover: IgGrayPrimitives[200],
    active: IgGrayPrimitives[600],
    disabled: IgGrayPrimitives[100],
    focused: IgGrayPrimitives[600],
  },
  dark: {
    default: IgGrayPrimitives["A500"],
    hover: IgGrayPrimitives["A400"],
    active: IgGrayPrimitives["A600"],
    disabled: IgGrayPrimitives[900],
    focused: IgGrayPrimitives["A600"],
  },
  error: {
    default: IgRedPrimitives["A100"],
    hover: IgRedPrimitives["A200"],
    active: IgRedPrimitives["A300"],
    disabled: IgRedPrimitives[700],
    focused: IgRedPrimitives["A400"],
  },
  warning: {
    default: IgOrangePrimitives[800],
    hover: IgOrangePrimitives[900],
    active: IgOrangePrimitives["A100"],
    disabled: IgOrangePrimitives[600],
    focused: IgOrangePrimitives["A200"],
  },
  success: {
    default: IgGreenPrimitives["A100"],
    hover: IgGreenPrimitives["A200"],
    active: IgGreenPrimitives["A300"],
    disabled: IgGreenPrimitives[400],
    focused: IgGreenPrimitives["A400"],
  },
} as const;

export const IgCoreBackgroundColors = {
  baseLightHigh: IgGrayPrimitives[50],
  baseLightMid: IgGrayPrimitives[100],
  baseLightLow: IgGrayPrimitives[200],
  baseDark: IgGrayPrimitives["A500"],
  accent: IgBluePrimitives[100],
  accentSolid: IgBluePrimitives["A200"],
  tonal: IgTonalBackgroundColors,
  interactive: IgInteractiveBackgroundColors,
} as const;

// Border colors
export const IgBorderColors = {
  core: {
    default: IgGrayPrimitives[800],
    hover: IgGrayPrimitives["A100"],
    active: IgGrayPrimitives["A200"],
    disabled: IgGrayPrimitives[600],
  },
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[500],
  },
  success: {
    default: IgGreenPrimitives[800],
    hover: IgGrayPrimitives["A100"],
    active: IgGrayPrimitives["A200"],
    disabled: IgGrayPrimitives[600],
  },
  error: {
    default: IgRedPrimitives[900],
    hover: IgRedPrimitives["A100"],
    active: IgRedPrimitives["A300"],
    disabled: IgRedPrimitives[700],
  },
  info: {
    default: IgAzurePrimitives[900],
    hover: IgAzurePrimitives["A100"],
    active: IgAzurePrimitives["A300"],
    disabled: IgAzurePrimitives[700],
  },
  warning: {
    default: IgOrangePrimitives[800],
    hover: IgOrangePrimitives[900],
    active: IgOrangePrimitives["A200"],
    disabled: IgOrangePrimitives[600],
  },
} as const;

// Icon colors
export const IgIconColors = {
  light: IgGrayPrimitives["A400"],
  lightHover: IgGrayPrimitives["A600"],
  dark: IgGrayPrimitives[200],
  darkHover: IgGrayPrimitives[500],
  accent: IgBluePrimitives["A100"],
  accentHover: IgBluePrimitives["A300"],
  info: IgAzurePrimitives["A100"],
  infoHover: IgAzurePrimitives["A300"],
  success: IgGreenPrimitives["A100"],
  successHover: IgGreenPrimitives["A300"],
  warning: IgOrangePrimitives["A200"],
  warningHover: IgOrangePrimitives["A100"],
  error: IgRedPrimitives["A200"],
  errorHover: IgRedPrimitives["A300"],
} as const;

// ===========================================================================
// AI design language tokens
// ---------------------------------------------------------------------------
// The single source of truth for the Intelligems "AI" visual language —
// iridescent gradients, faint surface washes, and the hover "shine". Exposed
// on the theme as `theme.igAi` (see the return value below) so the IgAi*
// components and any AI affordance share one palette instead of re-declaring
// near-identical gradient strings inline.
// ===========================================================================

/** SVG `<stop>`s for the 195° iridescent ramp, for inline `<linearGradient>` paint servers (IgAiIconGradient). */
export const AI_ICON_GRADIENT_STOPS = [
  { offset: "0%", color: "#FFA977" },
  { offset: "35%", color: "#F472B6" },
  { offset: "67%", color: "#8B5CF6" },
  { offset: "96%", color: "#5EEAD4" },
] as const;

/** The same 195° iridescent ramp as a CSS gradient — icon fills and masked monochrome assets. */
export const AI_ICON_GRADIENT = `linear-gradient(195deg, ${AI_ICON_GRADIENT_STOPS.map(
  (stop) => `${stop.color} ${stop.offset}`
).join(", ")})`;

/** 238° iridescent ramp painted on stroke borders and the IgAiButton frame. */
export const AI_STROKE_GRADIENT =
  "linear-gradient(238deg, #ffa977 5.4%, #ff85da 33.68%, #7d77ff 67.8%, #68ece6 95.59%)";

/**
 * Legible "dark" pink→blue ramp. The iridescent ramps above are too light to
 * read as text or to carry white text on top, so AI text accents and filled AI
 * surfaces use this darker ramp instead.
 */
export const AI_DARK_GRADIENT =
  "linear-gradient(219deg, #f65cdc 5.59%, #2600fa 109.91%)";

/** Faint iridescent surface wash, multiply-blended under content at a given opacity/angle. */
export const aiSurfaceWash = (alpha: number, angle = 195) =>
  `linear-gradient(${angle}deg, rgba(255,169,119,${alpha}) 5%, rgba(255,133,218,${alpha}) 34%, rgba(125,119,255,${alpha}) 68%, rgba(104,236,230,${alpha}) 96%)`;

/**
 * The Atlas brand tile's wash (Figma "Atlas / Rail header" › brand container):
 * the stroke gradient's angle and stops at 10% opacity, multiply-blended over
 * white. Not `aiSurfaceWash(0.1, 238)` — the tile keeps the stroke's own stops.
 */
export const AI_TILE_WASH =
  "linear-gradient(238deg, rgba(255,169,119,0.1) 5.4%, rgba(255,133,218,0.1) 33.7%, rgba(125,119,255,0.1) 67.8%, rgba(104,236,230,0.1) 95.6%)";

/** White highlight band parked off the left edge; swept across a surface on hover. */
export const AI_SHINE_BAND =
  "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.65) 50%, transparent 65%)";

export const BILLING_PROGRESS_GRADIENT_STOPS = [
  IgCyanPrimitives[400],
  IgBasePrimitives[500],
  "#6052FF",
  "#FC8F50",
] as const;

export const BILLING_PROGRESS_GRADIENT = `linear-gradient(90deg, ${BILLING_PROGRESS_GRADIENT_STOPS.join(
  ", "
)})`;
